<template>
  <div :id="pointWindowId" class="point-window uk-card" v-if="isShow" >
    <!-- Point見出し -->
    <div v-if="selected" class="point-header uk-card-header uk-flex uk-flex-between" :class="{backable: backable()}">
      <button class="back" type="button" aria-label="Back" @click="options.onBacked">
        <span uk-icon="icon: chevron-left"></span>
      </button>

      <div>
        <div v-if="kana.enabled && selected.extra_fields[kana.key]" class="kana">{{ selected.extra_fields[kana.key] }}</div>
        <h4 class="uk-card-title"> {{ selected.name }} </h4>
      </div>

      <button class="close" type="button" aria-label="Close" uk-close @click="options.onClosed"></button>
    </div>

    <!-- クラスタ見出し -->
    <div  v-else class="cluster-header uk-card-header uk-flex uk-flex-between">
      <h4 class="uk-card-title small" v-html="messages.common_points_found.replace('{points_count}', pointsInCluster.length)"></h4>
      <button class="close" type="button" aria-label="Close" uk-close @click="options.onClosed"></button>
    </div>

    <!-- Pointの属性・もしくはクラスタに所属するPoint -->
    <div ref="scrollarea"
      class="uk-card-body uk-padding-small uk-panel-scrollable overscroll-shadow"
      :class="{
        top: overflowDirection.top,
        bottom: overflowDirection.bottom,
        'uk-padding-remove': !selected,
        'with-footer': selected,
      }"
      @wheel.stop
      @touchstart.stop
      @scroll="onScroll">
      <!-- 選択したPoint -->
      <template v-if="selected">
        <div class="point-body">
          <div class="image-wrapper" v-if="thumbnailUrl">
            <img class="image" :src="thumbnailUrl">
          </div>

          <div class="content-wrapper">
            <div class="attribute" v-for="format in options.formats">
              <PointAttribute v-if="isShowAttribute(format.required_selected_ids)" :point="selected" :format="format"></PointAttribute>
            </div>
          </div>
        </div>
        <div class="conditions">
          <PointSearchConditions :point="selected" :showIds="showSearchConditionIds" place="point_window" v-if="options.searchConditions?.show"></PointSearchConditions>
        </div>
      </template>

      <!-- クラスタ -->
      <template v-else>
        <ul class="points">
          <li
            class="point-wrapper uk-flex uk-flex-between uk-flex-middle"
            v-for="point in pointsInCluster"
            :key="point.id"
            @click="options.onInnerPointSelected(point)"
            v-wave
          >
            <Point :point="point" @onToPointClicked="options.onInnerPointSelected(point)"></Point>
          </li>
        </ul>
      </template>
    </div>

    <!-- 選択したPoint用の操作 -->
    <div class="actions uk-text-center card-footer" v-if="selected && (usePointPage || showActions.length > 0)" :class="{bottom: overflowDirection.bottom}">
      <template v-for="action in showActions">
        <!-- リンク -->
        <a 
          v-if="action.href && (!action?.isHide || action.isHide())"
          class="action uk-button uk-button-small"
          :class="{
            [action.class]: true,
            'with-icon': action.icon
          }"
          :id="action.id"
          :href="getActionHref(action)"
          :target="action.target"
          v-wave
        >
          <i v-if="action.icon" class="material-symbols-sharp" :data-icon="action.icon"></i>
          <span class="text-modifier">
            {{ getActionLabel(action) }}
          </span>
        </a>

        <!-- ボタン -->
        <button class="action uk-button uk-button-small"
          :class="{
            [action.class]: true,
            'with-icon': action.icon
          }"
          v-if="!action.href && (!action?.isHide || action.isHide()) && organizationActions[action.onclick]"
          :id="action.id"
          @click="organizationActions[action.onclick](selected)"
          v-wave
        >
          <i v-if="action.icon" class="material-symbols-sharp" :data-icon="action.icon"></i>
          <span class="text-modifier">
            {{ getActionLabel(action) }}
          </span>
        </button>
      </template>

      <a class="to_point with-icon uk-button uk-button-primary uk-button-small"
        :href="Page.getPointUrl(selected)"
        :target="toPointLinkTarget"
        v-if="usePointPage"
        v-wave>
        <span class="text-modifier">
          {{ messages.common_to_point }}
        </span>
        <i class="material-symbols-sharp" data-icon="chevron_right"></i>
      </a>
    </div>
  </div>
</template>

<script setup>
/**
 * Point用のInfoWindow的に使うもの
 *
 * Point・クラスタの両方に対応
 * 単独ではマップ上に埋め込めないので、components.common.InfoWindow.jsに埋め込んで使う
 */
import { inject, ref, onMounted, watch, nextTick, computed } from 'vue'
import VWave from 'v-wave'

import { useStore } from 'vuex'

import Point from '@/components/common/Point.vue'
import PointAttribute from '@/components/common/PointAttribute.vue'
import PointSearchConditions from '@/components/common/PointSearchConditions.vue'
import { Filters } from '@/services/Filters.ts'

import PointService from "@/services/PointService.ts"
import Page from "@/services/Page"
import { parse } from '@/utils/format.ts'
import { getParams } from '@/utils/common'


/**
 * 起動時に渡されるもの
 * 構造は一旦PointService.initPointWindow参照
 */
const options = inject('pointWindow')

/**
 * iframeごしだと別途定義が必要
 */
const { vWave } = VWave.createLocalWaveDirective({})

const showSearchConditionIds = ref()
const kana = ref(storelocator.point_title_kana || { enabled: false })

const messages = computed(() => storelocator.messages)

// storeから店舗選択の詳細を参照
const store = useStore()
// 選択している店舗
const selected = computed(() => store.state.points.selected)
const selectedCluster = computed(() => store.state.points.selectedClusterId)
const pointsInCluster = computed(() => store.state.points.pointsInCluster)
const enabledThumbnail = computed(() => options.thumbnail?.enabled)
const thumbnailKey = computed(() => options.thumbnail?.key || "")
const organizationActions = computed(() => store.state.organizationActions)

/**
 * サムネイルのURLを返す
 * 画像がない場合は空文字を返す
 * @returns {String}
 */
const thumbnailUrl = computed(() => {
  // サムネイルなしの場合は空文字として表現
  const noThumbnail = ""

  // サムネイル表示条件が揃っていない場合は空文字を返す
  if (!selected.value) return noThumbnail
  if (!enabledThumbnail.value) return noThumbnail
  if (!thumbnailKey.value) return noThumbnail

  const imageField = selected.value.extra_fields[thumbnailKey.value]

  // imageFieldにはURLが格納されているため、そのまま返す
  return imageField || noThumbnail
})

/**
 * true: 店舗ページを使用
 */
const usePointPage = computed(() => storelocator.point?.enabled)
const toPointLinkTarget = computed(() => getParams().iframe != undefined ? '_self' : '_parent')

/**
 * PointWindowのID
 * ポイント固有のCSSを当てる場合に備えて、ポイント固有のIDを振る
 * IDにはポイントのkeyを使う point-window-${point.key}
 * クラスタの場合は共通IDとする point-window-cluster
 * @returns {String}
 */
const pointWindowId = computed(() => {
  if (selected.value) {
    return `point-window-${selected.value.key}`
  }
  return `point-window-cluster`
})

/**
 * @typedef {Object} Action
 * @property {string} [id] - アクションの一意のID
 * @property {string} label - 表示するラベル（messages内のキーか直接の文字列）
 * @property {string} [class] - ボタン/リンクに適用するCSSクラス
 * @property {Object} [href] - リンクの場合の設定
 * @property {string} [href.format] - 動的リンク生成のためのフォーマット文字列
 * @property {string} [target] - リンクのtarget属性（"_blank"など）
 * @property {Object} [need] - 表示条件となるpointのプロパティ
 * @property {string} [need.key] - 表示に必要なプロパティのキー名
 * @property {Object[]} [need.url_params] - 表示に必要なURLパラメータのキー名 [{key: value}]
 * @property {string} [onclick] - store.state.organizationActions に登録されている関数名を指定
 * 以下はコード上に記述あるものの、関数の指定方法がない。未実装？
 * @property {function} [isHide] - 非表示判定用の関数 （JSON保存なので関数は機能しないはず） () => boolean 
 */

/**
 * リンクはこれで十分
 *
 * リンク以外の場合
 * ここの初期化前にactionsを変更（関数を仕込むに必要）
 *
 * 行儀悪めではあるので、必要になったら改めて仕込み方を考えたほうがいいかも
 * @type Action[]
 */
const actions = ref(storelocator.map?.point_window?.actions || [])

/**
 * 画面に表示する actions のみを絞り込んだもの
 * この配列が空かつ、PointPageが無効の場合はフッターを非表示にする
 * @type Action[]
 */
const showActions = computed(() => actions.value.filter(action => isShowAction(action)))

// infoWindowの表示 / 非表示を計算
const isShow = computed(() => {
  if (!selected.value && !selectedCluster.value) {
    return null
  }
  return [!!selected.value, !!selectedCluster.value]
})

// 表示アニメーション、スクロールエフェクトの更新
onMounted(async () => {
  await nextTick()
  await new Promise(resolve => setTimeout(resolve, 100))
  onScroll()
})

watch(isShow, async () => {
  // console.debug('isShow', isShow.value)

  if (isShow.value) {
    // 選択した検索条件のみ表示
    if (options.searchConditions?.show && options.searchConditions?.only_selected) {
      // 表示する検索条件を更新
      showSearchConditionIds.value = getSelectedSearchConditionIds()
    }

    // 画面更新、アニメーション分遅延
    await nextTick()
    await new Promise(resolve => setTimeout(resolve, 100))
    if (!scrollarea.value) {
      return
    }

    scrollarea.value.scrollTo(0, 0)
    onScroll()
  } else {
    // ここで消さないと、矢印だけ残ってしまう
    PointService.closePoint()
  }
})

// スクロールエフェクト オーバーフローしている方向にシャドウをつける
const scrollarea = ref()
const overflowDirection = ref({
  top: false,
  bottom: false
})

async function onScroll() {
  const target = scrollarea.value
  if (!target?.offsetHeight || !target?.scrollHeight) {
    return
  }

  if (target.offsetHeight >= target.scrollHeight) {
    overflowDirection.value.bottom = false
    overflowDirection.value.top = false
    return
  }

  const scrollSpan = target.scrollHeight - target.offsetHeight
  const endScroll = scrollSpan - scrollSpan * 0.1
  overflowDirection.value.bottom = target.scrollTop > scrollSpan * 0.1
  overflowDirection.value.top = target.scrollTop < endScroll
}

function backable() {
  // console.debug('backable', PointService.returnCluster)
  return !!PointService.returnCluster
}

/**
 * 表示するアクションか判断
 * @param {Action} action actions[]
 * @returns {Boolean} 必要なフィールドを空以外で持っていれば表示として判断
 */
function isShowAction(action) {
  // 条件(need)がなければ常に表示
  if (!action.need) {
    return true
  }

  // キーが存在しないなら非表示
  if (action.need.key && !selected.value[action.need.key] && !selected.value.extra_fields[action.need.key]) {
    return false
  }

  // パラメータが存在しないなら非表示
  if(action.need.url_params && !checkMatchUrlParams(action.need.url_params)) {
    return false
  }

  return true
}

/**
 * URLパラメータを取得して指定されたパラメータと一致するか確認する
 * @typedef {Object.<string, string>} UrlParam
 * @param {UrlParam[]} params - 確認したいパラメータの配列 [{key: value}]
 * @returns {boolean} パラメータが全て一致する場合はtrue、それ以外はfalse
 */
function checkMatchUrlParams(params) {
  // 現在のURLからクエリパラメータを取得
  const currentParams = storelocator.url_params || {}
  for (const param of params) {
    for (const key in param) {
      // 指定されたキーがURL内に存在するか確認
      if (!Object.prototype.hasOwnProperty.call(currentParams, key)) {
        return false
      }

      // 値が一致するか確認
      if (currentParams[key] != param[key]) {
        return false
      }
    }
  }

  // すべてのチェックをパスした場合はtrueを返す
  return true
}

/**
 * ラベル取得
 * @param {Action} action actions[]
 * @returns {String} messagesにあれば採用、なければそのまま使う
 */
 function getActionLabel(action) {
  return messages.value[action.label] || action.label
}

/**
 * リンク用意
 * 主に電話番号を想定
 * @param {Action} action actions[]
 */
function getActionHref(action) {
  return parse(selected.value, {
    kind: 'text',
    format: action.href?.format || ''
  })
}

/**
 * 選択されている検索条件のIDを取得
 * @returns {Array} 選択されている検索条件のID
 */
function getSelectedSearchConditionIds() {
  // マップページ
  const searchConditionIds = Object.values(Filters.selectedFilters.search_conditions)

  if (searchConditionIds.length > 0) {
    return searchConditionIds
  }

  // マップページ以外
  if (sessionStorage) {
    const cache = Filters.cache.load()
    return cache?.search_conditions || []
  }
}

/**
 * Pointの属性を表示するか判断
 * 検索条件のいずれかが選択されている場合は表示
 * 親のidが指定されている場合は子孫のいずれかが選択されていれば表示
 * @param {Array} requiredIds 検索条件のID
 * @returns {Boolean} true: 表示
 */
function isShowAttribute(requiredIds) {
  // 表示条件が設定されていない場合は表示
  if (!requiredIds || !requiredIds.length) {
    return true
  }

  // 親のidが指定されている場合も考慮し、フラットにする
  const allRequiredIds = []
  requiredIds.forEach(id => {
    const filter = Filters.findById(Filters.TYPE_SEARCH_CONDITIONS, id)
    if (filter.children) {
      Filters.findChildren(filter).forEach(child => allRequiredIds.push(child.id))
    } else {
      allRequiredIds.push(id)
    }
  })

  const selectedIds = getSelectedSearchConditionIds()
  return allRequiredIds.some(id => selectedIds.includes(id))
}
</script>

<style lang="scss">
@keyframes point-window-fade-in {
  0% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

#PointWindow {
  animation: point-window-fade-in .5s ease-out;
  cursor: auto;
  position: absolute;
  width: calc(100vw - 2rem);
  max-width: 320px;
  z-index: 2;
  opacity: 1;
  visibility: visible;
  transition: all .25s;
  transform: translate(-50%, -100%) translate(0, -60px);
  margin: 0 0 1rem 0;
  &::after {
    content: "";
    display: block;
    width: 0;
    height: 0;
    border-left: 12px solid transparent;
    border-right: 12px solid transparent;
    border-top: solid 12px #fff;
    margin:auto;
  }

  &.ready {
    opacity: 0;
    visibility: hidden;
  }

  .point-window {
    background-color: rgba(255, 255, 255, .95);
    box-shadow: 0 0 6px rgba(0, 0, 0, .25);
    border-radius: 4px;

    .uk-card-header {
      background-color: var(--theme-color);
      border-radius: 4px 4px 0 0;
      padding: .65rem 1rem;

      .uk-card-title {
        color: var(--text-color);
        font-size: 1rem;
        font-weight: bold;
        margin: 0 1rem 0 0;
      }

      .kana {
        color: var(--text-color);
        font-size: 0.75rem;
        font-weight: normal;
        letter-spacing: 0.125rem;
        padding-left: 2px;
        margin: 0 1rem 0 0;
      }

      &.cluster-header {
        .uk-card-title {
          font-size: 0.75rem;
          font-weight: normal;
          .points-count {
            font-size: 1.25rem;
            margin: 0 0.5rem 0 0;
          }
        }
      }
      &.point-header {
        border: none;
      }
      button {
        background: none;
        cursor: pointer;
        border: none;
        color: var(--text-color);
        min-width: 1rem;
        flex-shrink: 0;
        margin-left: auto;

        &.back {
          margin: 0 .5rem 0 -0.25rem;
          display: none;
          &:hover {
            transform: translateX(-10%);
            transition: all .5s;
          }
        }
      }

      &.backable {
        .uk-card-title {
          margin: 2px .5rem 0 0;
        }
        .kana {
          margin: 2px .5rem 0 0;
          + .uk-card-title {
            margin: 0 .5rem 0 0;
          }
        }
        .back {
          display: block;
        }
      }
    }

    .card-footer {
      border-top: solid 1px #efefef;
      padding: .65rem 1rem;
      &.bottom {
        border-top: solid 1px #eaeaea;
      }
      a, button {
        flex-grow: 1;
        margin-left: 4px;
      }
    }

    .uk-card-body {
      max-height: 200px;
      &.with-footer {
        max-height: 150px;
        height: auto;
        @media only screen and (max-width: $breakpoint-small) {
          max-height: 110px;
        }
      }
    }

    .point-body {
      display: flex;

      .image-wrapper {
        display: flex;
        align-items: start;
        padding-right: 1rem;
        .image {
          width: 100px;
          height: 100px;
          border-radius: 4px;
          object-fit: cover;
        }
      }

      .content-wrapper {
        .attribute {
          font-size: .9rem;
          margin-bottom: .5rem;
          overflow-wrap: break-word;
          &:last-child {
            margin-bottom: 0;
          }
          p, a {
            margin: 0
          }

          // 空の場合はマージンを空けない
          &:empty {
            margin: 0;
          }

          .sub-title {
            font-size: .75rem;
            color: #999
          }
        }
      }
    }

    .points {
      padding: 0;

      .point-wrapper {
        border-bottom: solid 1px #eee;
        .point {
          padding: 0.2rem 0.4rem 0 0.4rem;
          width: 100%;

          .name {
            top: 12px;
            left: 38px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            width: calc(100% - 48px - 28px);
          }

          .kana {
            top: 9px;
            left: 38px;
            + .name {
              top: 15px;
            }
          }

          .marker {
            margin: 0 0 8px 0;
            transform: scale(0.8);
          }

          .arrow {
            transform: translateY(-50%) scale(1);
          }
          &:hover {
            .arrow {
              right: 0.75rem;
              opacity: .9;
              transition: all .25s;
            }
          }
        }
      }
    }

    .actions {
      a, button {
        border: 1px solid var(--text-color);
        border-radius: 15px;
        color: var(--theme-color-tp70);
        &:hover {
          background-color: var(--theme-color);
          border: 1px solid var(--theme-color);
          color: var(--text-color);
          transition: all .5s;
          opacity: 1;
        }
        &.with-icon {
          border-radius: 15px;
          padding: 0 2rem 0 1rem;
          position: relative;
          i {
            display: inline;
            position: absolute;
            top: 2px;
            right: .5rem;
            top: 50%;
            transform: translateY(-50%);
            opacity: .7;
          }
          &:hover {
            i {
              opacity: 1;
              right: .25rem;
              transition: all .5s;
            }
          }
          // アイコンの位置を左に移動
          &.icon-left {
            i {
              left: .5rem;
              right: auto;
            }
            &:hover {
              i {
                left: .25rem;
                right: auto;
              }
            }
          }
          // アイコンの位置を文頭に移動
          &.icon-inline {
            i {
              position: relative;
              font-size: 1.25rem;
              vertical-align: top;
              display: inline-block;
              transform: translate(0, 4px)
            }
            &:hover {
              i {
                right: .5rem;
              }
            }
          }
        }
        &.to_point {
          color: var(--text-color);
        }
      }
    }
  }
}
</style>
